import React, { useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";

import { ZodProvider } from "@autoform/zod";
import {
  useNotification,
  useParsed,
  useWarnAboutChange,
} from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { Button } from "@/components/ui/button";
import { useLoadingOverlay } from "@/contexts/loading-context";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { getDirtyValues, isFormDirty } from "@/lib/utils";
import { parseRead } from "@/lib/zod/read-schema";
import { ReadOnlyNote } from "@/pages/sports/components/read-only-note";
import { useSportsRepository } from "@/pages/sports/hooks/use-sports-repository";
import { TeamHomeVenueField } from "@/pages/teams/components/fields/team-home-venue-field";
import {
  TeamEditType,
  teamEditSchema,
  teamFactsForSubmit,
} from "@/pages/teams/schemas/team.edit.form";

/** Curator edit for a team: short name, city, home stadium, founded year, nicknames, Wikidata id, status,
 * hide, media. The rest is TINA's. */
export const TeamEdit = () => {
  const { getTeam, updateTeam } = useSportsRepository();
  const { id } = useParsed();
  const [values, setValues] = React.useState<TeamEditType>();
  const [source, setSource] = React.useState<Record<string, any>>();
  const { open } = useNotification();
  const { setLoading } = useLoadingOverlay();
  const { setWarnWhen, warnWhen } = useWarnAboutChange();
  const setTitle = useDocumentTitle();

  const schemaProvider = useMemo(() => new ZodProvider(teamEditSchema), []);

  useEffect(() => {
    if (!id) {
      open?.({
        type: "error",
        message: "Something went wrong. This shouldn't be happening",
      });
      return;
    }
    setLoading(true);
    getTeam(id.toString())
      .then((response) => {
        // getOne resolves the flat TeamDbDto, as in PlaysEdit.
        const raw = response as Record<string, any>;
        const data = parseRead(teamEditSchema, raw, "team") as TeamEditType;
        setSource(raw);
        setValues(data);
        setTitle("Edit " + (raw.name ?? data.shortName ?? id));
      })
      .catch((error) => {
        console.error(error);
        open?.({
          type: "error",
          message: "Failed to get team",
          description: toHumanErrorMessage(error),
        });
      })
      .finally(() => setLoading(false));
  }, [id]);

  const initializeForm = (
    form: UseFormReturn<TeamEditType, any, TeamEditType>,
  ) => {
    form.watch((value) => {
      setWarnWhen(
        form.formState.isDirty &&
          isFormDirty(
            form.formState.dirtyFields as Record<string, unknown>,
            value as Record<string, unknown>,
            form.formState.defaultValues as Record<string, unknown>,
          ),
      );
    });
  };

  const onSubmit = async (
    allValues: TeamEditType,
    form: UseFormReturn<TeamEditType, any, TeamEditType>,
  ) => {
    const dirty = getDirtyValues(
      form.formState.dirtyFields as Record<string, unknown>,
      allValues,
    );
    setWarnWhen(false);
    await updateTeam({ ...teamFactsForSubmit(dirty), id: id?.toString() });
  };

  return (
    <div className="flex flex-row">
      <div className="flex w-full flex-col space-y-4 p-8">
        {source && (
          <div className="space-y-1 text-sm">
            <div>
              <span className="text-muted-foreground">Highlightly ID: </span>
              <span className="font-mono">
                {source.metadata?.sourceId ?? "—"}
              </span>
            </div>
            <ReadOnlyNote />
          </div>
        )}
        {values && (
          <AutoForm
            schema={schemaProvider}
            defaultValues={values}
            onSubmit={onSubmit}
            onFormInit={initializeForm}
            {...injectCustomAutoFormComponents({
              formComponents: { teamHomeVenue: TeamHomeVenueField },
            })}
          >
            <BottomActionBar>
              <Button disabled={!warnWhen} type="submit">
                Submit
              </Button>
            </BottomActionBar>
          </AutoForm>
        )}
      </div>
    </div>
  );
};
